import { useUpdateSubscriptionLimitationsMutation } from "@api/accounts";
import { useGetSubscriptionLimitsQuery } from "@api/static-assets";
import Button from "@components/ui/button";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import debounce from "lodash.debounce";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useGetCurrentUserQuery } from "@/shared/services";

const WHITELISTED_EMAILS = ["vladz@storydoc.com", "dor@storydoc.com"];

const SubscriptionLimitations = () => {
  "use no memo";
  const { data: me } = useGetCurrentUserQuery();
  const { data: limitations } = useGetSubscriptionLimitsQuery({});
  const [updateSubscriptionLimitations, { isLoading }] =
    useUpdateSubscriptionLimitationsMutation();
  const ref = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const formatJson = () => {
    try {
      const parsed = JSON.parse(value.trim());
      const formatted = JSON.stringify(parsed, null, 2);
      if (ref.current) ref.current.value = formatted;
      setValue(formatted);
      setError(null);
      toast.success("JSON formatted successfully");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid JSON format");
      toast.error(
        `Cannot format invalid JSON: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    }
  };

  const handleContentChange = debounce((value: string) => {
    setValue(value);
    setError(null);
  }, 500);

  useEffect(() => {
    if (limitations) {
      const formattedJson = JSON.stringify(limitations, null, 2);
      if (ref.current) ref.current.value = formattedJson;
      setValue(formattedJson);
    }
  }, [limitations]);

  const onSubmit = async () => {
    if (isLoading) return;
    try {
      let jsonString = value.trim();
      jsonString = jsonString.replace(/}(\s*)"(\w+)":/g, '},$1"$2":');

      try {
        const data = JSON.parse(jsonString);

        const validPlanNames = ["trial", "starter", "pro", "team"];
        const isValidStructure = Object.entries(data).every(([, plans]) => {
          if (jsonString === "{}") return true;
          if (
            typeof plans !== "object" ||
            plans === null ||
            Array.isArray(plans)
          )
            return false;

          const planNames = Object.keys(plans);
          return planNames.every((planName) =>
            [...validPlanNames, "current"].includes(planName),
          );
        });

        if (!isValidStructure) {
          toast.error(
            "Invalid JSON structure. Each preset should contain only valid plan names: trial, starter, pro, team",
          );
          return;
        }

        await updateSubscriptionLimitations({ data }).unwrap();
        toast.success("Subscription limitations updated successfully");
      } catch (parseError) {
        console.error("Parse error:", parseError);
        toast.error(
          `JSON parsing error: ${parseError instanceof Error ? parseError.message : "Invalid JSON format"}`,
        );
        return;
      }
    } catch (error) {
      toast.error(
        "Failed to update subscription limitations: " +
          (error instanceof Error ? error.message : "Invalid JSON"),
      );
    }
  };

  if (!WHITELISTED_EMAILS.includes(me?.email || "")) return null;

  return (
    <>
      <hr className="tw-my-6 tw-border-t tw-border-blue-100" />

      <div>
        <span className="tw-mb-1 tw-block tw-font-sans tw-text-[13px]/normal tw-font-semibold tw-text-blue-base">
          Subscription limitations
        </span>
        <textarea
          ref={ref}
          onInput={(e) => handleContentChange(e.currentTarget.value)}
          className={cn(
            "scrollbar tw-flex tw-h-24 tw-w-full tw-resize-none tw-items-center tw-justify-start tw-rounded tw-border tw-border-solid tw-border-[#e7e6ec] tw-bg-white tw-px-3 tw-py-1 tw-font-sans tw-text-xs tw-font-normal tw-text-blue-base placeholder:tw-text-[#88819f] focus:tw-outline focus:tw-outline-2 focus:tw-outline-offset-2 focus:tw-outline-[#7b17cf33]",
            {
              "tw-border-red-200 tw-bg-red-50/50": false,
            },
          )}
        ></textarea>
        <div className="tw-mt-6 tw-flex tw-items-center tw-gap-2">
          <Button size="default" variant="secondary" onClick={onSubmit}>
            {isLoading ? (
              <>
                <Spinner className="tw-mr-2 tw-size-3 tw-border-blue" />
                Updating...
              </>
            ) : (
              "Update limitations"
            )}
          </Button>
          <Button size="default" variant="secondary" onClick={formatJson}>
            Format JSON
          </Button>
        </div>
        {error && (
          <div className="tw-mt-2 tw-text-sm tw-text-red-500">{error}</div>
        )}
      </div>
    </>
  );
};

export default SubscriptionLimitations;
